/* corex-textil-editor-geo.js · Geometría y dibujo del personalizador de camisetas (sin dependencias).
   Unidades: las capas y áreas se guardan en cm reales de la prenda.
   - Coordenadas de prenda (u, v): u = cm sobre la tela desde el eje central (+ derecha en pantalla),
     v = cm hacia abajo desde el cuello de la vista (delantero en "front", trasero en "back").
   - Coordenadas de capa (x, y): cm desde el centro del área de impresión.
   Escala: px/cm = (bottom − top) · altoImagen / largo(talla).
   Las fotos solo se usan en la VISTA PREVIA ("Así queda puesta"): ahí el diseño se adapta al cuerpo con pecho plano y
   costados curvos (curvedFrame) y se recorta con la silueta. El editor dibuja sobre la camiseta plana
   (corex-textil-editor-flat.js). */
(function () {
  'use strict';

  var BACK_NECK_DROP_CM = 1.5;      /* cuello trasero: 1,5 cm bajo el punto más alto del hombro */
  var SLEEVE_PX_PER_CM = 20;        /* unidades internas de la vista de manga */
  var SLEEVE_TOP_RATIO = 0.42;      /* ancho plano de la manga en la sisa ≈ 0,42 · ½ pecho */
  var SLEEVE_HEM_RATIO = 0.37;      /* ancho plano en el bajo de la manga */
    var STRIP_PX = 2;                 /* ancho de cada franja al curvar el diseño */
  var MAX_FLAT_PX = 4096;
  var TEXT_MEASURE_PX = 100;
  var DEFAULT_CAL = { top: 0.1876, bottom: 0.812, cuello_delantero: 0.256, cx: 0.5, manga_dx: 0.31, manga_cy: 0.36, manga_ang: 10 };

  var measureCtxCache = null;
  function measureCtx() {
    if (!measureCtxCache) measureCtxCache = document.createElement('canvas').getContext('2d');
    return measureCtxCache;
  }

  function clamp(v, min, max) { return Math.min(max, Math.max(min, v)); }
  function rad(deg) { return deg * Math.PI / 180; }
  function calibration(cal) { return Object.assign({}, DEFAULT_CAL, cal || {}); }

  /* ---------- marcos (de cm de prenda a px de la imagen/vista) ---------- */
  /* Pecho plano y costados curvos: hasta a cm del eje la tela va plana; de ahí al costado sigue un arco de radio R,
     de modo que el costado (½ pecho P/2 de tela) cae en el borde del torso de la foto (wp cm desde el eje). La foto es
     un maniquí 3D y su torso se ve más estrecho que la prenda en plano. Si la foto es casi un cilindro (a < 0), no hay
     tramo plano y R = wp. Más allá del costado la tela queda detrás (se pega al borde y la máscara la oculta). */
  function curveParams(half, wp) {
    if (!(wp > 0) || wp >= half) return null;
    var R = (half - wp) / (Math.PI / 2 - 1), a = wp - R;
    if (a < 0) { a = 0; R = wp; }
    return { a: a, R: R, maxU: a + R * Math.PI / 2 };
  }
  function curvedU(c, u) {
    var s = u < 0 ? -1 : 1, x = Math.abs(u);
    return s * (x <= c.a ? x : c.a + c.R * Math.sin(Math.min(Math.PI / 2, (x - c.a) / c.R)));
  }
  function curvedInvU(c, x) {
    var s = x < 0 ? -1 : 1, y = Math.abs(x);
    return s * (y <= c.a ? y : c.a + c.R * Math.asin(Math.min(1, (y - c.a) / c.R)));
  }
  /* Marco de foto curvado (vista previa): torsoHalfPx = mitad del ancho del torso en la foto (px de la foto). */
  function curvedFrame(frame, torsoHalfPx) {
    var c = curveParams(frame.med.pecho / 2, torsoHalfPx / frame.pxPerCm), k = frame.pxPerCm;
    if (!c) return frame;
    return Object.assign(frame, {
      curve: c, maxU: c.maxU,
      fwd: function (u, v) { return { x: frame.cx + curvedU(c, u) * k, y: frame.neckY + v * k }; },
      inv: function (x, y) { return { u: curvedInvU(c, (x - frame.cx) / k), v: (y - frame.neckY) / k }; }
    });
  }
  /* Marco de foto a escala real (pxPerCm en los dos ejes), centrado en el eje (cx). Sin torso medido queda plano;
     con él, curvedFrame. maxU limita el lienzo auxiliar del render plano. Los archivos de impresión se generan aparte,
     en plano. */
  var MAX_U_CM = 80;
  function planar(frame) {
    var k = frame.pxPerCm;
    return Object.assign(frame, {
      maxU: MAX_U_CM,
      fwd: function (u, v) { return { x: frame.cx + u * k, y: frame.neckY + v * k }; },
      inv: function (x, y) { return { u: (x - frame.cx) / k, v: (y - frame.neckY) / k }; }
    });
  }

  /* Vista delantera o trasera (foto "back" o silueta, con el mismo encuadre que la frontal). W y H = tamaño real
     de la foto cargada. Cuello trasero: cal.cuello_trasero (medido en la foto) o, sin él, 1,5 cm bajo el hombro. */
  function backNeckY(cal, H, pxPerCm) {
    return cal.cuello_trasero != null ? cal.cuello_trasero * H : cal.top * H + BACK_NECK_DROP_CM * pxPerCm;
  }
  function garmentFrame(W, H, calIn, med, view, torsoHalfPx) {
    var cal = calibration(calIn);
    var pxPerCm = (cal.bottom - cal.top) * H / med.largo;
    var neckY = view === 'back' ? backNeckY(cal, H, pxPerCm) : cal.cuello_delantero * H;
    /* sudaderas con capucha o cuello alto: el encuadre empieza en lo más alto de la silueta (cal.visible_top), no en el
       punto alto del hombro del que se mide el largo */
    var y0 = (Math.min(cal.top, cal.visible_top == null ? cal.top : cal.visible_top) - 0.035) * H, y1 = (cal.bottom + 0.025) * H;
    var frame = planar({
      kind: view, W: W, H: H, cal: cal, med: med,
      pxPerCm: pxPerCm, cx: cal.cx * W, neckY: neckY,
      hemV: (cal.bottom * H - neckY) / pxPerCm,
      view: { x: W * 0.1, y: y0, w: W * 0.8, h: y1 - y0 }
    });
    return torsoHalfPx ? curvedFrame(frame, torsoHalfPx) : frame;
  }

  /* Vista ampliada de manga: tubo visto de lado. Ancho plano estimado desde ½ pecho; largo = medida de manga. */
  function sleeveFrame(med) {
    var k = SLEEVE_PX_PER_CM, L = med.manga || 17;
    var wTop = med.pecho * SLEEVE_TOP_RATIO, wHem = med.pecho * SLEEVE_HEM_RATIO;
    var rTop = wTop / Math.PI, rHem = wHem / Math.PI;
    var viewW = (2 * rTop + 9) * k, viewH = (L + 7) * k;
    return planar({
      kind: 'sleeve', med: med, L: L, rTop: rTop, rHem: rHem,
      pxPerCm: k, cx: viewW / 2, neckY: 3.5 * k, hemV: L,
      view: { x: 0, y: 0, w: viewW, h: viewH }
    });
  }

  /* Vista de manga sobre la foto lateral. cal.manga = caja de la manga en fracciones de la foto
     { top: hombro, bottom: bajo, left, right, bajo_left, bajo_right }. Escala: (bottom − top) · H / largo de manga.
     Los diseños se dibujan planos (sin curvar el tubo). mirror = la otra manga (foto en espejo):
     solo se refleja la foto; los diseños no. */
  function photoSleeveFrame(W, H, calIn, med, mirror) {
    var box = calibration(calIn).manga, L = med.manga || 17;
    var k = (box.bottom - box.top) * H / L, halfW = (box.right - box.left) * W / 2;
    var cxPhoto = (box.left + box.right) / 2 * W, cx = mirror ? W - cxPhoto : cxPhoto;
    var top = box.top * H, padX = halfW * 0.9, padTop = 0.045 * H, padBottom = 0.03 * H;
    return planar({
      kind: 'sleeve', photo: true, mirror: !!mirror, W: W, H: H, box: box, med: med, L: L,
      pxPerCm: k, cx: cx, neckY: top, hemV: L,
      view: { x: cx - halfW - padX, y: top - padTop, w: 2 * (halfW + padX), h: box.bottom * H + padBottom - (top - padTop) }
    });
  }
  /* Recorte de la manga en la foto lateral: copa curva arriba, laterales de la caja y bajo más estrecho. */
  function photoSleevePath(frame) {
    var b = frame.box, W = frame.W, H = frame.H;
    var fx = function (f) { return frame.mirror ? W - f * W : f * W; };
    var l = fx(b.left), r = fx(b.right), bl = fx(b.bajo_left != null ? b.bajo_left : b.left), br = fx(b.bajo_right != null ? b.bajo_right : b.right);
    var top = b.top * H, hem = b.bottom * H, cap = (hem - top) * 0.22, cx = frame.cx;
    var p = new Path2D();
    p.moveTo(l, top + cap);
    p.bezierCurveTo(l + (cx - l) * 0.25, top, cx - (cx - l) * 0.4, top, cx, top);
    p.bezierCurveTo(cx + (r - cx) * 0.4, top, r - (r - cx) * 0.25, top, r, top + cap);
    p.lineTo(br, hem);
    p.lineTo(bl, hem);
    p.closePath();
    return p;
  }
  /* Dibuja la foto (reflejada si es la otra manga) en px de la foto. */
  function drawPhoto(ctx, img, frame) {
    if (!frame.mirror) { ctx.drawImage(img, 0, 0, frame.W, frame.H); return; }
    ctx.save();
    ctx.translate(frame.W, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(img, 0, 0, frame.W, frame.H);
    ctx.restore();
  }

  /* ---------- áreas de impresión (cm de prenda) ---------- */
  function sizeOf(value) {
    var p = String(value || '10x10').split('x');
    return { w: Number(p[0]) || 10, h: Number(p[1]) || 10 };
  }
  var SLEEVE_HEM_DIST = 2;
  /* Área de una impresión en cm de prenda: { w, h, top (v del borde superior), cu (u del centro) }.
     print: { size, dist, dx }. En las mangas va centrada en horizontal y dist se mide del bajo de la manga al borde
     inferior (v = 0 en el hombro): top = largo de manga − dist − alto. dist null (aún sin rellenar) = 2 cm. */
  function sleeveTop(L, dist, h) { return L - (dist == null ? SLEEVE_HEM_DIST : Number(dist)) - h; }
  function printArea(kind, print, med) {
    var s = sizeOf(print.size);
    if (kind === 'sleeve') return { w: s.w, h: s.h, top: sleeveTop(med.manga || 17, print.dist, s.h), cu: 0 };
    return { w: s.w, h: s.h, top: Number(print.dist) || 0, cu: Number(print.dx) || 0 };
  }
  function areaCenterV(area) { return area.top + area.h / 2; }

  /* ---------- capas ---------- */
  function textFont(l, px) { return (l.weight || 700) + ' ' + px + 'px ' + l.family; }
  function layerSize(l) {
    if (l.type === 'text') {
      var mc = measureCtx();
      mc.font = textFont(l, TEXT_MEASURE_PX);
      var w = mc.measureText(l.text || ' ').width / TEXT_MEASURE_PX * l.size;
      return { w: Math.max(w, l.size * 0.4), h: l.size * 1.2 };
    }
    return { w: l.w, h: l.w * l.nh / l.nw };
  }
  function layerCorners(l) {
    var s = layerSize(l), a = rad(l.rotation || 0), c = Math.cos(a), sn = Math.sin(a);
    return [[-1, -1], [1, -1], [1, 1], [-1, 1]].map(function (k) {
      var x = k[0] * s.w / 2, y = k[1] * s.h / 2;
      return { x: l.x + x * c - y * sn, y: l.y + x * sn + y * c };
    });
  }
  function isOutside(l, area) {
    var eps = 0.05;
    return layerCorners(l).some(function (p) { return Math.abs(p.x) > area.w / 2 + eps || Math.abs(p.y) > area.h / 2 + eps; });
  }
  /* Caja del conjunto de capas visibles (cm, relativa al centro de la impresión) o null si no hay ninguna. */
  function designBounds(layers) {
    var pts = [];
    layers.forEach(function (l) { if (!l.hidden) pts = pts.concat(layerCorners(l)); });
    if (!pts.length) return null;
    var xs = pts.map(function (p) { return p.x; }), ys = pts.map(function (p) { return p.y; });
    var x0 = Math.min.apply(null, xs), x1 = Math.max.apply(null, xs), y0 = Math.min.apply(null, ys), y1 = Math.max.apply(null, ys);
    return { w: x1 - x0, h: y1 - y0, cx: (x0 + x1) / 2, cy: (y0 + y1) / 2 };
  }

  function toLocal(l, p) {
    var dx = p.x - l.x, dy = p.y - l.y, a = -rad(l.rotation || 0);
    return { x: dx * Math.cos(a) - dy * Math.sin(a), y: dx * Math.sin(a) + dy * Math.cos(a) };
  }
  function fromLocal(l, p) {
    var a = rad(l.rotation || 0);
    return { x: l.x + p.x * Math.cos(a) - p.y * Math.sin(a), y: l.y + p.x * Math.sin(a) + p.y * Math.cos(a) };
  }

  /* Dibuja capas en un contexto cuyo origen es el centro del área y cuya unidad es 1 cm. */
  function drawLayers(ctx, layers, getImage) {
    layers.forEach(function (l) {
      if (l.hidden) return;
      ctx.save();
      ctx.translate(l.x, l.y);
      ctx.rotate(rad(l.rotation || 0));
      if (l.type === 'image') {
        var img = getImage(l.src), s = layerSize(l);
        if (img && img.complete && img.naturalWidth) ctx.drawImage(img, -s.w / 2, -s.h / 2, s.w, s.h);
      } else {
        var f = 1 / TEXT_MEASURE_PX * l.size;
        ctx.scale(f, f);
        ctx.font = textFont(l, TEXT_MEASURE_PX);
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = l.color;
        ctx.fillText(l.text, 0, 0);
      }
      ctx.restore();
    });
  }

  /* Renderiza las capas en plano (sin curvar) en un lienzo auxiliar que cubre el área y todas las capas. */
  function renderFlat(layers, area, frame, k, getImage, cache) {
    var visible = layers.filter(function (l) { return !l.hidden; });
    var cv = areaCenterV(area), cu = area.cu || 0;
    var u0 = cu - area.w / 2, u1 = cu + area.w / 2, v0 = area.top, v1 = area.top + area.h;
    visible.forEach(function (l) {
      layerCorners(l).forEach(function (p) {
        u0 = Math.min(u0, p.x + cu); u1 = Math.max(u1, p.x + cu);
        v0 = Math.min(v0, p.y + cv); v1 = Math.max(v1, p.y + cv);
      });
    });
    u0 = Math.max(u0, -frame.maxU); u1 = Math.min(u1, frame.maxU);
    v0 = Math.max(v0, -8); v1 = Math.min(v1, frame.hemV + 4);
    var scale = Math.min(k, MAX_FLAT_PX / Math.max(u1 - u0, 1), MAX_FLAT_PX / Math.max(v1 - v0, 1));
    var canvas = (cache && cache.canvas) || document.createElement('canvas');
    canvas.width = Math.max(1, Math.ceil((u1 - u0) * scale));
    canvas.height = Math.max(1, Math.ceil((v1 - v0) * scale));
    var ctx = canvas.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(scale, 0, 0, scale, (cu - u0) * scale, (cv - v0) * scale);
    drawLayers(ctx, visible, getImage);
    return { canvas: canvas, u0: u0, v0: v0, k: scale, empty: !visible.length };
  }

  /* Coloca el render plano con fwd(u) en franjas verticales (curva el diseño en la vista previa). ctx en px de imagen. */
  function warpBlit(ctx, flat, frame, overlapPx) {
    if (flat.empty) return;
    var c = flat.canvas, top = frame.fwd(0, flat.v0).y, h = c.height / flat.k * frame.pxPerCm;
    for (var sx = 0; sx < c.width; sx += STRIP_PX) {
      var sw = Math.min(STRIP_PX, c.width - sx);
      var ua = flat.u0 + sx / flat.k, ub = flat.u0 + (sx + sw) / flat.k;
      if (Math.abs(ua) >= frame.maxU && Math.abs(ub) >= frame.maxU && ua * ub > 0) continue;
      var xa = frame.fwd(ua, 0).x, xb = frame.fwd(ub, 0).x;
      if (xb - xa <= 0) continue;
      ctx.drawImage(c, sx, 0, sw, c.height, xa, top, xb - xa + (overlapPx || 0), h);
    }
  }

  /* Polilínea proyectada de un rectángulo en coordenadas de capa/área (para áreas y selección). */
  function projectedQuad(frame, cornersUV, steps) {
    var pts = [];
    for (var i = 0; i < 4; i++) {
      var a = cornersUV[i], b = cornersUV[(i + 1) % 4];
      for (var s = 0; s < steps; s++) {
        var t = s / steps;
        pts.push(frame.fwd(a.u + (b.u - a.u) * t, a.v + (b.v - a.v) * t));
      }
    }
    return pts;
  }
  function tracePath(ctx, pts) {
    ctx.beginPath();
    pts.forEach(function (p, i) { if (i) ctx.lineTo(p.x, p.y); else ctx.moveTo(p.x, p.y); });
    ctx.closePath();
  }

  /* ---------- color de la prenda y siluetas ---------- */
  /* Color medio de la foto en el pecho (zona central, ignorando píxeles transparentes). */
  function sampleColor(img, calIn) {
    var cal = calibration(calIn);
    try {
      var w = 80, h = Math.round(80 * img.naturalHeight / img.naturalWidth);
      var c = document.createElement('canvas');
      c.width = w; c.height = h;
      var ctx = c.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, w, h);
      var x0 = Math.round(w * (cal.cx - 0.12)), y0 = Math.round(h * (cal.cuello_delantero + 0.08));
      var d = ctx.getImageData(x0, y0, Math.round(w * 0.24), Math.round(h * 0.18)).data;
      var sum = [0, 0, 0], n = 0;
      for (var i = 0; i < d.length; i += 4) {
        if (d[i + 3] < 200) continue;
        sum[0] += d[i]; sum[1] += d[i + 1]; sum[2] += d[i + 2]; n++;
      }
      if (!n) return null;
      return 'rgb(' + sum.map(function (s) { return Math.round(s / n); }).join(',') + ')';
    } catch (e) {
      return null; /* imagen sin CORS: el llamador usa un gris neutro */
    }
  }
  function isLight(color) {
    var m = String(color).match(/\d+/g);
    if (!m) return false;
    return (0.299 * m[0] + 0.587 * m[1] + 0.114 * m[2]) > 200;
  }

  /* Espalda: la silueta de la foto (alfa) teñida con el color de la prenda, sombreado lateral y cuello trasero. */
  function backSilhouette(img, frame, color) {
    var W = frame.W, H = frame.H, cal = frame.cal;
    var c = document.createElement('canvas');
    c.width = W; c.height = H;
    var ctx = c.getContext('2d');
    ctx.drawImage(img, 0, 0, W, H);
    ctx.globalCompositeOperation = 'source-in';
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, W, H);
    ctx.globalCompositeOperation = 'source-atop';
    var g = ctx.createLinearGradient(W * 0.1, 0, W * 0.9, 0);
    g.addColorStop(0, 'rgba(0,0,0,0.22)');
    g.addColorStop(0.3, 'rgba(0,0,0,0.04)');
    g.addColorStop(0.5, 'rgba(255,255,255,0.05)');
    g.addColorStop(0.7, 'rgba(0,0,0,0.04)');
    g.addColorStop(1, 'rgba(0,0,0,0.22)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    /* cuello trasero: canalé curvo cerca del borde superior (más alto que el delantero) */
    var neckHalf = W * 0.078, y = frame.neckY;
    ctx.lineCap = 'round';
    ctx.strokeStyle = 'rgba(0,0,0,0.38)';
    ctx.lineWidth = frame.pxPerCm * 0.3;
    ctx.beginPath();
    ctx.moveTo(cal.cx * W - neckHalf, cal.top * H + frame.pxPerCm * 0.2);
    ctx.quadraticCurveTo(cal.cx * W, y + frame.pxPerCm * 0.6, cal.cx * W + neckHalf, cal.top * H + frame.pxPerCm * 0.2);
    ctx.stroke();
    ctx.strokeStyle = 'rgba(0,0,0,0.14)';
    ctx.lineWidth = frame.pxPerCm * 0.12;
    ctx.beginPath();
    ctx.moveTo(cal.cx * W - neckHalf * 1.05, cal.top * H + frame.pxPerCm * 1.2);
    ctx.quadraticCurveTo(cal.cx * W, y + frame.pxPerCm * 1.7, cal.cx * W + neckHalf * 1.05, cal.top * H + frame.pxPerCm * 1.2);
    ctx.stroke();
    return c;
  }

  /* Manga vista de lado: tubo con copa curva arriba y bajo con pespunte. ctx en unidades de la vista. */
  function sleevePath(frame) {
    var k = frame.pxPerCm, cx = frame.cx, top = frame.neckY;
    var wt = frame.rTop * k, wh = frame.rHem * k, hem = top + frame.L * k;
    var p = new Path2D();
    p.moveTo(cx - wt, top + 2.4 * k);
    p.bezierCurveTo(cx - wt * 0.7, top - 0.6 * k, cx + wt * 0.7, top - 0.6 * k, cx + wt, top + 2.4 * k);
    p.lineTo(cx + wh, hem);
    p.quadraticCurveTo(cx, hem + 0.6 * k, cx - wh, hem);
    p.closePath();
    return p;
  }
  function drawSleeve(ctx, frame, color) {
    var k = frame.pxPerCm, path = sleevePath(frame), hem = frame.neckY + frame.L * k;
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.22)';
    ctx.shadowBlur = 12;
    ctx.fillStyle = color;
    ctx.fill(path);
    ctx.restore();
    ctx.save();
    ctx.clip(path);
    var g = ctx.createLinearGradient(frame.cx - frame.rTop * k, 0, frame.cx + frame.rTop * k, 0);
    g.addColorStop(0, 'rgba(0,0,0,0.25)');
    g.addColorStop(0.5, 'rgba(255,255,255,0.06)');
    g.addColorStop(1, 'rgba(0,0,0,0.25)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, frame.view.w, frame.view.h);
    ctx.strokeStyle = 'rgba(0,0,0,0.2)';
    ctx.lineWidth = 0.08 * k;
    ctx.setLineDash([0.35 * k, 0.25 * k]);
    ctx.beginPath();
    ctx.moveTo(frame.cx - frame.rHem * k, hem - 1.6 * k);
    ctx.quadraticCurveTo(frame.cx, hem - 1.0 * k, frame.cx + frame.rHem * k, hem - 1.6 * k);
    /* costura de la copa (unión con el cuerpo) */
    var wt = frame.rTop * k, top = frame.neckY;
    ctx.moveTo(frame.cx - wt, top + 3.4 * k);
    ctx.bezierCurveTo(frame.cx - wt * 0.7, top + 0.4 * k, frame.cx + wt * 0.7, top + 0.4 * k, frame.cx + wt, top + 3.4 * k);
    ctx.stroke();
    ctx.restore();
    ctx.save();
    ctx.font = '600 ' + 0.75 * k + 'px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillStyle = '#8C867E';
    ctx.fillText('Vista lateral · hombro arriba', frame.cx, top - 1.6 * k);
    ctx.restore();
    if (isLight(color)) {
      ctx.save();
      ctx.strokeStyle = 'rgba(0,0,0,0.18)';
      ctx.lineWidth = 0.06 * k;
      ctx.stroke(path);
      ctx.restore();
    }
    return path;
  }

  window.CorexTextilGeo = {
    BACK_NECK_DROP_CM: BACK_NECK_DROP_CM,
    clamp: clamp, rad: rad, calibration: calibration, sizeOf: sizeOf,
    garmentFrame: garmentFrame, curveParams: curveParams, curvedU: curvedU, sleeveFrame: sleeveFrame, photoSleeveFrame: photoSleeveFrame, photoSleevePath: photoSleevePath, drawPhoto: drawPhoto, sleeveTop: sleeveTop, printArea: printArea, areaCenterV: areaCenterV,
    layerSize: layerSize, layerCorners: layerCorners, isOutside: isOutside, toLocal: toLocal, fromLocal: fromLocal,
    drawLayers: drawLayers, renderFlat: renderFlat, warpBlit: warpBlit, projectedQuad: projectedQuad, tracePath: tracePath,
    designBounds: designBounds,
    sampleColor: sampleColor, isLight: isLight, backSilhouette: backSilhouette, drawSleeve: drawSleeve, sleevePath: sleevePath
  };
})();
